import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { api } from '../../../lib/api';

type PlatformSettings = {
  legal_terms_version: string;
  legal_privacy_version: string;
  legal_cookies_version: string;
};

type BillingInfo = {
  mode: 'test' | 'live';
  configured: { secret_key: boolean; webhook_secret: boolean; price_basic: boolean; price_full: boolean };
};

type DemoAccessCode = {
  code?: string | null;
  updated_at?: string | null;
};

export default function PlatformSettingsPanel() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [demoCode, setDemoCode] = useState<DemoAccessCode | null>(null);
  const [rotatingDemoCode, setRotatingDemoCode] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sResp, bResp, dResp] = await Promise.all([
        api.get('/api/admin/platform/settings'),
        api.get('/api/billing/platform/info'),
        api.get('/api/admin/platform/demo/access-code'),
      ]);
      setSettings(sResp.data as any);
      setBillingInfo(bResp.data as any);
      setDemoCode(dResp.data as any);
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut încărca setările.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const resp = await api.put('/api/admin/platform/settings', settings);
      setSettings(resp.data as any);
      alert('Setări salvate. Utilizatorii vor fi rugați să re-acceptă documentele la următorul refresh/login.');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut salva setările.');
    } finally {
      setSaving(false);
    }
  }, [settings]);

  const cfg = billingInfo?.configured;
  const demoLinkBase = useMemo(() => {
    const raw = String((import.meta as any)?.env?.VITE_DEMO_PORTAL_URL || '').trim();
    if (raw) return raw;
    const host = String(window.location.hostname || '').toLowerCase();
    if (host === 'scola.ro' || host === 'www.scola.ro') return 'https://demo.scola.ro/test';
    return `${window.location.origin}/test`;
  }, []);
  const isDemoUrlExplicit = useMemo(() => Boolean(String((import.meta as any)?.env?.VITE_DEMO_PORTAL_URL || '').trim()), []);

  const rotateDemo = useCallback(async () => {
    setRotatingDemoCode(true);
    try {
      const resp = await api.post('/api/admin/platform/demo/access-code/rotate', {});
      setDemoCode(resp.data as any);
      alert('Cod demo generat.');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut genera codul.');
    } finally {
      setRotatingDemoCode(false);
    }
  }, []);

  const copyToClipboard = useCallback(async (text: string) => {
    const value = String(text || '').trim();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      alert('Copiat.');
    } catch {
      window.prompt('Copiază manual:', value);
    }
  }, []);

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-lg font-semibold text-foreground">Legal (versiuni)</div>
            <div className="text-sm text-muted-foreground">
              Schimbarea versiunilor va cere utilizatorilor să re-acceptă Termenii / Politica de confidențialitate / Cookies.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" iconName="RefreshCw" onClick={() => void load()} disabled={loading}>
              Reîncarcă
            </Button>
            <Button variant="default" iconName="Save" onClick={() => void save()} disabled={saving || loading || !settings}>
              Salvează
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          <Input
            label="Termeni (YYYY-MM-DD)"
            type="date"
            value={settings?.legal_terms_version || ''}
            onChange={(e) => setSettings((p) => (p ? { ...p, legal_terms_version: e.target.value } : p))}
          />
          <Input
            label="Confidențialitate (YYYY-MM-DD)"
            type="date"
            value={settings?.legal_privacy_version || ''}
            onChange={(e) => setSettings((p) => (p ? { ...p, legal_privacy_version: e.target.value } : p))}
          />
          <Input
            label="Cookies (YYYY-MM-DD)"
            type="date"
            value={settings?.legal_cookies_version || ''}
            onChange={(e) => setSettings((p) => (p ? { ...p, legal_cookies_version: e.target.value } : p))}
          />
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-start gap-2">
          <Icon name="CreditCard" size={16} className="text-muted-foreground mt-0.5" />
          <div>
            <div className="text-lg font-semibold text-foreground">Stripe</div>
            <div className="text-sm text-muted-foreground">Status configurare (fără a afișa chei secrete).</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mt-4">
          <div className="border border-border rounded-lg p-3">
            <div className="text-xs text-muted-foreground">Mod</div>
            <div className="text-lg font-semibold text-foreground">{billingInfo?.mode ? billingInfo.mode.toUpperCase() : '—'}</div>
          </div>
          <div className="border border-border rounded-lg p-3">
            <div className="text-xs text-muted-foreground">Secret key</div>
            <div className="text-sm font-medium text-foreground">{cfg ? (cfg.secret_key ? 'OK' : 'Lipsește') : '—'}</div>
          </div>
          <div className="border border-border rounded-lg p-3">
            <div className="text-xs text-muted-foreground">Webhook secret</div>
            <div className="text-sm font-medium text-foreground">{cfg ? (cfg.webhook_secret ? 'OK' : 'Lipsește') : '—'}</div>
          </div>
          <div className="border border-border rounded-lg p-3">
            <div className="text-xs text-muted-foreground">Price Basic</div>
            <div className="text-sm font-medium text-foreground">{cfg ? (cfg.price_basic ? 'OK' : 'Lipsește') : '—'}</div>
          </div>
          <div className="border border-border rounded-lg p-3">
            <div className="text-xs text-muted-foreground">Price Full</div>
            <div className="text-sm font-medium text-foreground">{cfg ? (cfg.price_full ? 'OK' : 'Lipsește') : '—'}</div>
          </div>
        </div>

        <div className="mt-4 text-sm text-muted-foreground">
          Pentru LIVE: setezi cheile live + prices live + webhook live către <code>/api/billing/webhook</code> și schimbi domeniile HTTPS.
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2">
            <Icon name="FlaskConical" size={16} className="text-muted-foreground mt-0.5" />
            <div>
              <div className="text-lg font-semibold text-foreground">Cod demo</div>
              <div className="text-sm text-muted-foreground">
                Un singur cod activ. Când generezi altul, cel vechi devine invalid.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" iconName="RefreshCw" onClick={() => void load()} disabled={loading || rotatingDemoCode}>
              Reîncarcă
            </Button>
            <Button variant="default" iconName="Wand2" onClick={() => void rotateDemo()} disabled={loading || rotatingDemoCode}>
              Generează
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          <Input label="Cod" value={demoCode?.code || ''} readOnly />
          <Input
            label="Link"
            value={
              demoCode?.code
                ? `${demoLinkBase}${demoLinkBase.includes('?') ? '&' : '?'}code=${encodeURIComponent(String(demoCode.code))}`
                : ''
            }
            readOnly
          />
          <Input
            label="Actualizat"
            value={demoCode?.updated_at ? new Date(demoCode.updated_at).toLocaleString() : '—'}
            readOnly
          />
        </div>

        {!isDemoUrlExplicit ? (
          <div className="mt-2 text-xs text-muted-foreground">
            Notă: `VITE_DEMO_PORTAL_URL` nu este setat în acest build. Link-ul folosește domeniul curent.
          </div>
        ) : null}

        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <Button variant="secondary" iconName="Copy" onClick={() => void copyToClipboard(String(demoCode?.code || ''))} disabled={!demoCode?.code}>
            Copiază cod
          </Button>
          <Button
            variant="outline"
            iconName="Link"
            onClick={() =>
              void copyToClipboard(
                demoCode?.code
                  ? `${demoLinkBase}${demoLinkBase.includes('?') ? '&' : '?'}code=${encodeURIComponent(String(demoCode.code))}`
                  : ''
              )
            }
            disabled={!demoCode?.code}
          >
            Copiază link
          </Button>
        </div>
      </div>
    </div>
  );
}
